import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import debounce from 'lodash/debounce';
import { FAStateContext } from '../context/FAStateContext';
import { AspectRatioContainer } from './Slider';
import { ContentContext } from '../context/ContentContext';
import { useFeatureAppEnv } from '../context/FeatureAppEnvContext';
import { countryCodeMap } from '../utils/countryCodeMap';
import ErrorMessage from './3DErrorMessage';

interface ExceptionEvent extends Event {
  error: Error;
  message: string;
}

const largeScreenConfig = {
  animations: true,
  close: false,
  zoom: false,
  cameraFlight: false,
  cameraNavigation: true,
  environments: true,
  interior: true,
  animationSpots: true,
};

const smallScreenConfig = {
  animations: false,
  close: false,
  zoom: false,
  cameraFlight: false,
  cameraNavigation: true,
  environments: false,
  interior: false,
  animationSpots: false,
};

export const ThreeDShowroomContent: React.FC = () => {
  const { viewMode, setViewMode, aveString } = useContext(FAStateContext);

  const { 'gfa:locale-service': locale, 's2:logger': logger } = useFeatureAppEnv().featureServices;
  const {
    streamDisplayOption: { show3dStream },
  } = useContext(ContentContext);
  const [isStreamActive, setIsStreamActive] = useState(false);
  const [is3DVisible, setIs3DVisible] = useState(viewMode === '3dStream');
  const [hasAVPError, setHasAVPError] = useState(false);
  const scriptLoadedRef = useRef(false);
  const [configId] = useState('a-customizer-default-2025');
  const [features, setFeatures] = useState(smallScreenConfig);
  const PrString = aveString || '';
  const marketIsoCode = countryCodeMap[locale.countryCode] || 'UNKOWN';

  const handleError = () => {
    setViewMode('slides');
    setIsStreamActive(false);
    setIs3DVisible(false);
    setHasAVPError(false);
  };

  // configure 3d Deck by device size
  useEffect(() => {
    const updateConfigBasedOnScreenSize = () => {
      const screenWidth = window.innerWidth;

      if (screenWidth > 1024) {
        // Large screens
        // option to set custom config for screensize?
        // setConfigId("audi-default");
        setFeatures(largeScreenConfig);
      } else {
        // Small screens
        // option to set custom config for screensize?
        // setConfigId("audi-default");
        setFeatures(smallScreenConfig);
      }
    };

    const debouncedResizeHandler = debounce(updateConfigBasedOnScreenSize, 250);
    updateConfigBasedOnScreenSize();
    window.addEventListener('resize', debouncedResizeHandler);

    return () => {
      // Cleanup: remove event listener
      window.removeEventListener('resize', debouncedResizeHandler);
      debouncedResizeHandler.cancel();
    };
  }, []);

  useEffect(() => {
    if (viewMode === '3dStream' && !scriptLoadedRef.current) {
      const script = document.createElement('script');
      script.src = 'https://avp-3dws-deck.eu-west-1-web-streaming.avp.tech/avp-3dws-deck.js';
      script.type = 'module';

      script.onload = () => {
        scriptLoadedRef.current = true;
      };

      script.onerror = (err) => {
        logger.error('Failed to load script for 3dStream:', err);
        scriptLoadedRef.current = false;
      };

      document.body.appendChild(script);
    }
  }, [viewMode, logger]);

  useEffect(() => {
    const handleDisconnect = () => {
      setViewMode('slides');
      setIsStreamActive(false);
      setIs3DVisible(false);
    };

    const handleException = (event: ExceptionEvent) => {
      logger.error(
        'axvp-3dws-deck - An unrecoverable error exception occurred. The error is',
        event.error,
      );
      setHasAVPError(true);
    };
    document.addEventListener('disconnected', handleDisconnect as EventListener);
    document.addEventListener('exception', (event) => handleException(event as ExceptionEvent));

    return () => {
      document.removeEventListener('disconnected', handleDisconnect as EventListener);
      document.removeEventListener('exception', (event) =>
        handleException(event as ExceptionEvent),
      );
    };
  }, []);

  useEffect(() => {
    if (viewMode === '3dStream') {
      setIs3DVisible(true);
      setIsStreamActive(true);
    } else {
      setIs3DVisible(false);
    }
  }, [viewMode]);

  if (!show3dStream || !aveString) {
    return null;
  }
  if (hasAVPError) {
    return <ErrorMessage onClose={handleError} />;
  }
  return (
    <Styled3dwsWrapper isActive={viewMode === '3dStream' && is3DVisible}>
      {aveString && (
        <avp-3dws-deck
          config-id={configId}
          active={isStreamActive}
          pr-string={PrString}
          market={marketIsoCode}
          features={JSON.stringify(features)}
          design={process.env.IS_LEGACY ? '' : 'alpha-customizer'}
        />
      )}
    </Styled3dwsWrapper>
  );
};

const Styled3dwsWrapper = styled(AspectRatioContainer)<{ isActive: boolean }>`
  position: absolute;
  display: ${(props) => (props.isActive ? 'block' : 'none')};
  top: 0;
  left: 0;
  z-index: 2;
`;
